import { Drawer, type DrawerProps } from 'antd';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SVGProps,
} from 'react';
import styled from 'styled-components';

/**
 * Shared drawer chrome (title, subtitle, close, spacing). Colours come from
 * each app’s `--color-*` tokens. Do not import generate-tax `index.scss`.
 *
 * generate.TAX keeps integrated-toolbar drawers in its own container and
 * passes `integrated` / slots here so padding and close stay aligned.
 */

const IconDrawerClose = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 23 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    {...props}
  >
    <path
      d="M16.1003 4.95821L11.3137 9.74478L6.52714 4.95821L4.78657 6.69878L9.57314 11.4854L4.78657 16.2719L6.52714 18.0125L11.3137 13.2259L16.1003 18.0125L17.8408 16.2719L13.0543 11.4854L17.8408 6.69878L16.1003 4.95821Z"
      fill="currentColor"
    />
  </svg>
);

const DrawerContentWrapper = styled.div<{
  $integrated?: boolean;
  $integratedCloseInset?: boolean;
  $customPadding?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 0%;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  padding: ${({ $customPadding, $integrated, $integratedCloseInset }) =>
    $customPadding ??
    ($integrated
      ? $integratedCloseInset
        ? '4px 4px 0 18px'
        : '4px 18px 0 18px'
      : '24px 24px 0 24px')};

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    flex-shrink: 0;
    margin-bottom: 21px;
  }

  .drawer-header.drawer-header--bottom-sheet {
    touch-action: none;
    cursor: grab;
  }

  .drawer-header.drawer-header--integrated {
    position: relative;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    padding-right: 28px;
  }

  .drawer-header.drawer-header--integrated.drawer-header--integrated-no-close {
    padding-right: 0;
  }

  .drawer-header-title-row {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-shrink: 0;
    min-width: 0;
  }

  .drawer-header-title-extra {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .drawer-header-toolbar-slot {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 0;
    padding-right: 0;
  }

  .drawer-title.drawer-title--integrated-block {
    flex-shrink: 0;
    min-width: 0;
    max-width: clamp(220px, 34vw, 340px);
  }

  .drawer-close-button.drawer-close-button--integrated-corner {
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
    z-index: 3;
    align-self: unset;
  }

  .drawer-title {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 600px;
    font-family: var(--font-family, Inter, sans-serif);
  }

  .drawer-bottom-sheet-handle {
    flex-shrink: 0;
    width: 100%;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: -4px 0 8px;
  }

  .drawer-bottom-sheet-handle::before {
    content: '';
    display: block;
    width: 48px;
    height: 4px;
    border-radius: 2px;
    background: var(--color-border, #dbeae3);
  }

  /* PWA integrated sheets hide the title row — keep a dedicated pull-down strip. */
  .drawer-header.drawer-header--pwa-drag-only {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0 4px;
    padding: 4px 0 0;
    min-height: 28px;
    cursor: grab;
    touch-action: none;
    user-select: none;
    flex-shrink: 0;

    .drawer-bottom-sheet-handle {
      margin: 0;
      height: 28px;
    }

    &:active {
      cursor: grabbing;
    }
  }

  .drawer-header-drag-wrap {
    flex-shrink: 0;
    width: 100%;
    touch-action: none;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .drawer-title-main {
    font-size: 22px;
    font-weight: 400;
    line-height: 24px;
    text-align: start;
    color: var(--color-text-dark, var(--color-text-base, #042a2a));
  }

  .drawer-title-sub {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    text-align: start;
    color: var(--color-text-muted, #5a6a6a);
  }

  .drawer-close-button {
    cursor: pointer;
    border: 1px solid transparent;
    width: 25px;
    height: 25px;
    border-radius: 5px;
    padding: 0;
    margin: 0;
    flex-shrink: 0;
    position: relative;
    z-index: 5;
    background: transparent;
    color: var(--color-text-dark, var(--color-text-base, #042a2a));
    display: flex;
    align-items: center;
    justify-content: center;
    /* Expand tap target without shifting layout */
    &::before {
      content: '';
      position: absolute;
      inset: -10px;
    }
    &:hover {
      border: 1px solid var(--color-primary, #41aaaf);
    }
    &:focus-visible {
      outline: 2px solid var(--color-primary, #41aaaf);
      outline-offset: 2px;
    }
  }

  .drawer-content {
    flex: 1 1 0%;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  /* Connect Entity (and other tile grids) must not grow to fill the drawer —
     leftover height would appear as a gap between Ant Rows. */
  .drawer-content > .ant-row,
  .drawer-content > .connect-entity {
    flex: 0 0 auto;
  }

  .drawer-content > .user-rights-panel,
  .drawer-content > .ant-spin-nested-loading,
  .drawer-content > .drawer-panel-root,
  .drawer-content > .review-document-root {
    flex: 1 1 0%;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }
`;

export type AppDrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  width?: number;
  zIndex?: number;
  closeAriaLabel?: string;
  maskClosable?: boolean;
  hideCloseButton?: boolean;
  contentKey?: string;
  contentPadding?: string;
  integrated?: boolean;
  titleExtra?: ReactNode;
  toolbar?: ReactNode;
  className?: string;
  rootClassName?: string;
  contentClassName?: string;
  /** PWA phone nav: drawer uses the full viewport width instead of the 400/800 clamp. */
  fullViewportWidth?: boolean;
  /** PWA phone nav: slide up from the bottom (Create submission, Connect entity, etc.). */
  placement?: DrawerProps['placement'];
  /** Height when `placement` is `bottom` (Ant ignores this for side placements). */
  height?: DrawerProps['height'];
  /** PWA integrated review: hide title row so the toolbar can use the full header. */
  hideIntegratedTitle?: boolean;
  toolbarClassName?: string;
  /**
   * When false, AppDrawer does not render the bottom-sheet drag notch (body owns it).
   * Default true for bottom placement.
   */
  showBottomSheetHandle?: boolean;
};

export const AppDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  width = 400,
  zIndex = 1200,
  closeAriaLabel = 'Close',
  maskClosable = true,
  hideCloseButton = false,
  contentKey,
  contentPadding,
  integrated = false,
  titleExtra,
  toolbar,
  className,
  rootClassName,
  contentClassName,
  fullViewportWidth = false,
  placement = 'right',
  height,
  hideIntegratedTitle = false,
  toolbarClassName,
  showBottomSheetHandle = true,
}: AppDrawerProps) => {
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth,
  );
  const headerDragRef = useRef<HTMLDivElement | null>(null);
  const sheetWrapperRef = useRef<HTMLElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startY: number;
    offsetY: number;
  } | null>(null);

  const getVisibleSheetWrapper = useCallback((): HTMLElement | null => {
    const root =
      headerDragRef.current?.closest('.ant-drawer') ??
      headerDragRef.current?.closest('.scope-pwa-drawer-root');
    if (!root) return null;
    // CSSMotion can leave a hidden wrapper in the DOM — only move the visible one.
    const wrappers = root.querySelectorAll<HTMLElement>(
      '.ant-drawer-content-wrapper'
    );
    for (let i = 0; i < wrappers.length; i += 1) {
      const el = wrappers[i];
      if (!el.classList.contains('ant-drawer-content-wrapper-hidden')) {
        return el;
      }
    }
    return wrappers[0] ?? null;
  }, []);

  const clearSheetTransform = useCallback((wrapper: HTMLElement | null) => {
    if (!wrapper) return;
    wrapper.style.removeProperty('transform');
    wrapper.style.removeProperty('transition');
  }, []);

  const setSheetOffset = useCallback(
    (wrapper: HTMLElement | null, offsetPx: number, animate: boolean) => {
      if (!wrapper) return;
      wrapper.style.setProperty(
        'transform',
        `translate3d(0, ${Math.max(0, offsetPx)}px, 0)`,
        'important'
      );
      wrapper.style.setProperty(
        'transition',
        animate ? 'transform 220ms ease-out' : 'none',
        'important'
      );
    },
    []
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const showCloseButton = hideCloseButton !== true;
  const isBottomSheet = placement === 'bottom';
  const responsiveWidth = isBottomSheet
    ? '100%'
    : fullViewportWidth
      ? viewportWidth > 0
        ? viewportWidth
        : '100%'
      : viewportWidth > 0
        ? Math.max(320, Math.min(width, viewportWidth - 24))
        : width;
  const sheetHeight = isBottomSheet ? (height ?? 'auto') : undefined;
  const bodyKey = contentKey ?? 'drawer';
  const autoSizedBottomSheet = isBottomSheet && sheetHeight === 'auto';
  // Above typical app chrome / PWA bottom nav (1200) so mask + close receive taps.
  const resolvedZIndex = isBottomSheet ? Math.max(zIndex, 1250) : zIndex;

  const resetDrag = useCallback(() => {
    clearSheetTransform(sheetWrapperRef.current);
    sheetWrapperRef.current = null;
    dragStateRef.current = null;
  }, [clearSheetTransform]);

  useEffect(() => {
    if (!open) resetDrag();
  }, [open, resetDrag]);

  const finishHeaderDrag = useCallback(
    (shouldClose: boolean) => {
      const state = dragStateRef.current;
      dragStateRef.current = null;
      if (!state) return;

      const wrapper = sheetWrapperRef.current ?? getVisibleSheetWrapper();
      if (shouldClose && wrapper) {
        setSheetOffset(wrapper, wrapper.clientHeight || window.innerHeight, true);
        window.setTimeout(() => {
          resetDrag();
          onClose();
        }, 220);
        return;
      }

      clearSheetTransform(wrapper);
      sheetWrapperRef.current = null;
    },
    [clearSheetTransform, getVisibleSheetWrapper, onClose, resetDrag, setSheetOffset]
  );

  const onHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!isBottomSheet || !open) return;
      const target = event.target as HTMLElement;
      if (target.closest('button, a, [role="button"], input, textarea, select')) {
        return;
      }
      const zone = headerDragRef.current;
      if (!zone) return;
      const wrapper = getVisibleSheetWrapper();
      if (!wrapper) return;

      // Hide any leftover CSSMotion wrappers so only one sheet can paint.
      const root = wrapper.closest('.ant-drawer');
      root?.querySelectorAll('.ant-drawer-content-wrapper-hidden').forEach((node) => {
        (node as HTMLElement).style.setProperty('display', 'none', 'important');
      });

      sheetWrapperRef.current = wrapper;
      dragStateRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        offsetY: 0,
      };
      setSheetOffset(wrapper, 0, false);
      zone.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [getVisibleSheetWrapper, isBottomSheet, open, setSheetOffset]
  );

  const onHeaderPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      const offset = Math.max(
        0,
        Math.min(event.clientY - state.startY, 520)
      );
      state.offsetY = offset;
      setSheetOffset(sheetWrapperRef.current, offset, false);
      event.preventDefault();
    },
    [setSheetOffset]
  );

  const onHeaderPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      const zone = headerDragRef.current;
      if (zone?.hasPointerCapture(event.pointerId)) {
        zone.releasePointerCapture(event.pointerId);
      }
      finishHeaderDrag(state.offsetY >= 72);
      event.preventDefault();
    },
    [finishHeaderDrag]
  );

  const onHeaderPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      const zone = headerDragRef.current;
      if (zone?.hasPointerCapture(event.pointerId)) {
        zone.releasePointerCapture(event.pointerId);
      }
      resetDrag();
      event.preventDefault();
    },
    [resetDrag]
  );

  const abortHeaderDrag = useCallback(() => {
    const state = dragStateRef.current;
    if (!state) return;
    const zone = headerDragRef.current;
    try {
      if (zone?.hasPointerCapture(state.pointerId)) {
        zone.releasePointerCapture(state.pointerId);
      }
    } catch {
      // Pointer may already be released when a nested overlay opens.
    }
    resetDrag();
  }, [resetDrag]);

  // Nested modals (e.g. New rule over Capture rules) can cancel pointer capture mid-drag.
  useEffect(() => {
    if (!isBottomSheet || !open) return;

    const onDocumentPointerEnd = (event: PointerEvent) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      const zone = headerDragRef.current;
      if (zone?.contains(event.target as Node)) return;
      abortHeaderDrag();
    };

    const onDocumentPointerCancel = (event: PointerEvent) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      abortHeaderDrag();
    };

    document.addEventListener('pointerup', onDocumentPointerEnd, true);
    document.addEventListener('pointercancel', onDocumentPointerCancel, true);
    return () => {
      document.removeEventListener('pointerup', onDocumentPointerEnd, true);
      document.removeEventListener('pointercancel', onDocumentPointerCancel, true);
    };
  }, [abortHeaderDrag, isBottomSheet, open]);

  const handleCloseClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    },
    [onClose]
  );

  const closeButton = showCloseButton ? (
    <button
      type="button"
      className={`drawer-close-button${
        integrated ? ' drawer-close-button--integrated-corner' : ''
      }`}
      onClick={handleCloseClick}
      onPointerDown={(event) => event.stopPropagation()}
      aria-label={closeAriaLabel}
    >
      <IconDrawerClose />
    </button>
  ) : null;

  const headerDragProps = isBottomSheet
    ? {
        ref: headerDragRef,
        onPointerDown: onHeaderPointerDown,
        onPointerMove: onHeaderPointerMove,
        onPointerUp: onHeaderPointerUp,
        onPointerCancel: onHeaderPointerCancel,
      }
    : {};

  const resolvedRootClassName = [
    rootClassName,
    isBottomSheet ? 'scope-pwa-drawer-root' : undefined,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <Drawer
      placement={placement}
      onClose={onClose}
      open={open}
      closable={false}
      maskClosable={maskClosable}
      keyboard
      width={responsiveWidth}
      height={sheetHeight}
      zIndex={resolvedZIndex}
      className={className}
      rootClassName={resolvedRootClassName}
      destroyOnClose
      {...(isBottomSheet
        ? {
            // Disable Ant panel motion so CSSMotion does not keep a second
            // transform layer that paints as a duplicate while dragging.
            motion: () => ({
              motionName: '',
              motionAppear: false,
              motionEnter: false,
              motionLeave: false,
            }),
          }
        : {})}
      style={{
        background: 'var(--color-drawer-bg, var(--color-layout-bg, #eaf6f7))',
      }}
      styles={{
        mask: {
          background: 'var(--color-bg-mask, rgba(0, 0, 0, 0.45))',
          pointerEvents: 'auto',
        },
        wrapper: isBottomSheet
          ? {
              // Single sheet surface — avoid a second painted panel behind content.
              background: 'transparent',
            }
          : undefined,
        content: isBottomSheet
          ? {
              background: 'var(--color-drawer-bg, var(--color-layout-bg, #eaf6f7))',
            }
          : undefined,
        body: {
          padding: '24px 24px 0',
          display: 'flex',
          flexDirection: 'column',
          height: autoSizedBottomSheet ? 'auto' : '100%',
          overflow: autoSizedBottomSheet ? 'visible' : 'hidden',
          background: 'var(--color-drawer-bg, var(--color-layout-bg, #eaf6f7))',
        },
      }}
    >
      <DrawerContentWrapper
        className={contentClassName}
        $integrated={integrated}
        $integratedCloseInset={Boolean(integrated && showCloseButton)}
        $customPadding={contentPadding}
      >
        {integrated ? (
          hideIntegratedTitle && isBottomSheet && showBottomSheetHandle ? (
            <div
              className="drawer-header drawer-header--bottom-sheet drawer-header--pwa-drag-only"
              aria-label={closeAriaLabel}
              {...headerDragProps}
            >
              <div className="drawer-bottom-sheet-handle" aria-hidden />
            </div>
          ) : hideIntegratedTitle && isBottomSheet && !showBottomSheetHandle ? null : (
            <div
              className={`drawer-header drawer-header--integrated${
                showCloseButton ? '' : ' drawer-header--integrated-no-close'
              }${hideIntegratedTitle ? ' drawer-header--pwa-toolbar-only' : ''}${
                isBottomSheet && !hideIntegratedTitle
                  ? ' drawer-header--bottom-sheet'
                  : ''
              }`}
              {...(isBottomSheet && !hideIntegratedTitle
                ? headerDragProps
                : {})}
            >
              {hideIntegratedTitle ? null : (
                <div className="drawer-header-title-row">
                  <div className="drawer-title drawer-title--integrated-block">
                    <span className="drawer-title-main">{title}</span>
                    {subtitle ? (
                      <span className="drawer-title-sub">{subtitle}</span>
                    ) : null}
                  </div>
                  {titleExtra ? (
                    <div className="drawer-header-title-extra">
                      {titleExtra}
                    </div>
                  ) : null}
                </div>
              )}
              <div
                className={`drawer-header-toolbar-slot${
                  toolbarClassName ? ` ${toolbarClassName}` : ''
                }`}
              >
                {toolbar}
              </div>
              {closeButton}
            </div>
          )
        ) : (
          <div
            className={
              isBottomSheet && showBottomSheetHandle
                ? 'drawer-header-drag-wrap'
                : undefined
            }
            {...(isBottomSheet && showBottomSheetHandle ? headerDragProps : {})}
          >
            {isBottomSheet && showBottomSheetHandle ? (
              <div className="drawer-bottom-sheet-handle" aria-hidden />
            ) : null}
            <div
              className={`drawer-header${
                isBottomSheet ? ' drawer-header--bottom-sheet' : ''
              }`}
            >
              <div className="drawer-title">
                <span className="drawer-title-main">{title}</span>
                {subtitle ? (
                  <span className="drawer-title-sub">{subtitle}</span>
                ) : null}
              </div>
              {closeButton}
            </div>
          </div>
        )}
        <div className="drawer-content" key={bodyKey}>
          {children}
        </div>
      </DrawerContentWrapper>
    </Drawer>
  );
};
